/**
 * defa.views.InvoiceUpdateForm
 * A form for updating invoices.
 * @version 1.2.1
 */
import { defa, T } from 'defa-object';
import Form        from 'scripts/poppet/form';
import template    from 'templates/invoiceUpdateForm.jst.html';

const channelUser = Bb.Radio.channel( 'user' );

const selectors = {
	buttonCreate  : '[data-click=update-create-usage]',
	buttonOriginal: '[data-click=update-counter-invoice]',
};

const InvoiceUpdateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	ui: {
		original: selectors.buttonOriginal,
		create  : selectors.buttonCreate,
	},
	
	events: {
		'click @ui.create'  : 'onClickCreate',
		'click @ui.original': 'onClickOriginal',
	},
	
	modelEvents: {
		'sync': 'render',
	},
	
	templateContext: function () {
		return {
			customerName    : this.model.getCustomer().name(),
			customerAddr1   : this.model.getCustomer().get( 'address1' ),
			customerAddr2   : this.model.getCustomer().get( 'address2' ),
			customerPostCode: this.model.getCustomer().get( 'postcode' ),
			customerCity    : this.model.getCustomer().get( 'city' ),
			customerCountry : this.model.getCustomer().get( 'countryCode' ),
			termStatus      : this.getStatus(),
			classHeader     : this.getHeaderClass(),
			dateCreated     : this.getDate( 'created' ),
			dateIssued      : this.getDate( 'issued' ),
			dateDue         : this.getDate( 'due' ),
			usages          : this.model.getUsages(),
			vat             : this.getVAT(),
			
			isTechSupport: channelUser.request( 'is:techsupport' ),
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when the user clicks the create button.
	 * @void
	 */
	onClickCreate: function (event) {
		event.preventDefault();
		this.trigger( 'create:usage' );
	},
	
	/**
	 * Triggers when the end user clicks the original button.
	 * @void
	 */
	onClickOriginal: function (event) {
		event.preventDefault();
		this.trigger( 'nav', 'invoice:update', [ this.model.get( 'transientCounterInvoice' ) ] );
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Gets a className for the header to use.
	 * @return {string} Classes for the h5.
	 */
	getHeaderClass: function () {
		let className = '';
		
		if ( -1 !== _.indexOf( [ 'PENDING', 'OPEN' ], this.model.get( 'status' ) ) ) {
			className = 'dot dot-' + this.model.get( 'status' ).toLowerCase();
		}
		
		if ( this.model.get( 'counterInvoice' ) ) {
			className = 'dot dot-counter';
		}
		
		return className;
	},
	
	/**
	 * Gets the translated term of this invoice's status.
	 * @return {string} The translated status.
	 */
	getStatus: function () {
		return _.where( defa.vars.invoiceStatuses, { value: this.model.get( 'status' ), } )[ 0 ].term;
	},
	
	/**
	 * Gets a date from the model, and returns it in short form.
	 * @param  {string} dateType  The key to one of the date attributes on this model.
	 * @return {string}           The date in short form or a no-break-space if the date doesn't exist.
	 */
	getDate: function ( dateType ) {
		
		if ( !this.model.get( dateType ) ) {
			return '';
		} else {
			
			let date = new Date( this.model.get( dateType ) );
			return date.format( T.dateFormatShort );
		}
	},
	
	/**
	 * Gets the VAT from the usages.
	 * @return {string} Returns the vat percentage if they are the same, returns nothing if different or — if no
	 *     usages are found.
	 */
	getVAT: function () {
		let usages = this.model.getUsages();
		
		if ( 0 >= usages.length ) {
			return '—';
		} else {
			let vats = _.uniq( usages.pluck( 'transientFormatVat' ) );
			
			if ( 1 < vats.length ) {
				return '';
			} else {
				return vats[ 0 ];
			}
		}
	},
	
} );

export default InvoiceUpdateForm;